package org.lennycodes.gradematewebsite


import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.autoLength
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.lennycodes.gradematewebsite.util.DownloadOption

// ---------------------------------------------------------------------------
// Editor state
// ---------------------------------------------------------------------------
data class DownloadEditState(
    val id: String?,
    val platform: String,
    val version: String,
    val size: String,
    val accentHex: String,
    val href: String,
    val description: String
)

private fun DownloadOption.toEditState(id: String) = DownloadEditState(
    id = id,
    platform = platform,
    version = version,
    size = size,
    accentHex = colorToHexLocal(accent),
    href = href,
    description = description
)

private val PLATFORM_OPTIONS = listOf("Android", "Windows", "Linux", "macOS", "iOS", "Web")

private val PRESET_COLORS = listOf(
    "#6366F1", "#22C55E", "#F472B6", "#06B6D4",
    "#FBBF24", "#8B5CF6", "#10B981", "#38BDF8",
    "#0EA5E9", "#EF4444", "#6B7280"
)

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
@Page("/admin")
@Composable
fun AdminDownloadsPage() {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()

    when (isAuthenticated) {
        null -> {
            Column(
                Modifier.fillMaxSize().padding(top = 120.px).gap(12.px),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                P(Modifier.fontSize(16.px).color(Colors.Gray).toAttrs()) {
                    Text("Checking session…")
                }
            }
            return
        }
        false -> {
            LaunchedEffect(Unit) { ctx.router.navigateTo(Routes.login) }
            return
        }
        true -> Unit
    }

    var downloads by remember { mutableStateOf<List<Pair<String, DownloadOption>>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var errorMessage by remember { mutableStateOf("") }
    var editing by remember { mutableStateOf<DownloadEditState?>(null) }

    LaunchedEffect(Unit) {
        try {
            downloads = fetchDownloadsWithIds()
        } catch (e: Throwable) {
            errorMessage = "Failed to load downloads: ${e.message}"
        } finally {
            isLoading = false
        }
    }

    Div(
        Modifier
            .fillMaxWidth()
            .padding(top = 120.px, bottom = 96.px)
            .toAttrs()
    ) {
        Column(
            Modifier
                .maxWidth(1200.px)
                .margin(0.px, autoLength)
                .padding(left = 24.px, right = 24.px)
                .gap(32.px)
        ) {
            // Header
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column(Modifier.gap(6.px)) {
                    H2(
                        Modifier.fontSize(30.px).fontWeight(FontWeight.Bold)
                            .margin(0.px).toAttrs()
                    ) { Text("Manage Downloads") }
                    P(
                        Modifier.fontSize(14.px).color(Colors.Gray).margin(0.px).toAttrs()
                    ) {
                        Text(
                            if (downloads.isEmpty())
                                "Create, update, and delete download options shown on the website."
                            else
                                "${downloads.size} download${if (downloads.size == 1) "" else "s"}"
                        )
                    }
                }
                Button(onClick = { editing = emptyDownloadEditState() }) {
                    Text("+ New Download")
                }
            }

            if (errorMessage.isNotEmpty()) {
                Div(
                    Modifier
                        .fillMaxWidth()
                        .padding(14.px, 16.px)
                        .backgroundColor(Color.rgb(220, 38, 38).withAlpha(0.1f))
                        .color(Color.rgb(220, 38, 38))
                        .borderRadius(10.px)
                        .toAttrs()
                ) { Text(errorMessage) }
            }

            if (isLoading) {
                Column(
                    Modifier.fillMaxWidth().padding(48.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    P(Modifier.fontSize(15.px).color(Colors.Gray).toAttrs()) {
                        Text("Loading downloads…")
                    }
                }
                return@Column
            }

            if (downloads.isEmpty()) {
                Div(
                    Modifier
                        .fillMaxWidth()
                        .padding(56.px, 48.px)
                        .textAlign(TextAlign.Center)
                        .borderRadius(14.px)
                        .backgroundColor(Color.rgb(99, 102, 241).withAlpha(0.05f))
                        .border(1.px, LineStyle.Dashed, Color.rgb(99, 102, 241).withAlpha(0.25f))
                        .toAttrs()
                ) {
                    P(Modifier.fontSize(15.px).color(Colors.Gray).margin(0.px).toAttrs()) {
                        Text("No downloads yet. Click \"+ New Download\" to add one.")
                    }
                }
            }

            if (downloads.isNotEmpty()) {
                Column(Modifier.fillMaxWidth().gap(16.px)) {
                    downloads.forEach { (id, download) ->
                        DownloadAdminCard(
                            download = download,
                            onEdit = { editing = download.toEditState(id) },
                            onDelete = {
                                scope.launch {
                                    try {
                                        deleteDownload(id)
                                        downloads = fetchDownloadsWithIds()
                                    } catch (e: Throwable) {
                                        errorMessage = "Delete failed: ${e.message}"
                                    }
                                }
                            }
                        )
                    }
                }
            }
        }
    }

    editing?.let { state ->
        DownloadEditorDialog(
            state = state,
            onDismiss = { editing = null },
            onSave = { updated ->
                scope.launch {
                    try {
                        val option = updated.toDownloadOption()
                        if (state.id == null) {
                            createDownload(option)
                        } else {
                            updateDownload(state.id, option)
                        }
                        editing = null
                        downloads = fetchDownloadsWithIds()
                        errorMessage = ""
                    } catch (e: Throwable) {
                        errorMessage = "Save failed: ${e.message}"
                    }
                }
            }
        )
    }
}

private fun emptyDownloadEditState() = DownloadEditState(
    id = null,
    platform = PLATFORM_OPTIONS.first(),
    version = "",
    size = "",
    accentHex = "#6366F1",
    href = "",
    description = ""
)

private fun DownloadEditState.toDownloadOption() = DownloadOption(
    platform = platform,
    version = version.trim().ifBlank { "v1.0.0" },
    size = size.trim().ifBlank { "—" },
    accent = hexToColor(accentHex),
    href = href.trim(),
    description = description.trim()
)

// ---------------------------------------------------------------------------
// Admin card
// ---------------------------------------------------------------------------
@Composable
private fun DownloadAdminCard(
    download: DownloadOption,
    onEdit: () -> Unit,
    onDelete: () -> Unit
) {
    var confirmDelete by remember { mutableStateOf(false) }

    Div(
        Modifier
            .fillMaxWidth()
            .padding(20.px)
            .backgroundColor(Colors.White)
            .borderRadius(14.px)
            .border(1.px, LineStyle.Solid, Colors.Black.withAlpha(0.06f))
            .boxShadow(0.px, 2.px, 12.px, 0.px, Colors.Black.withAlpha(0.05f))
            .toAttrs()
    ) {
        Row(
            Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(14.px),
                modifier = Modifier.weight(1f)
            ) {
                Div(
                    Modifier
                        .width(48.px)
                        .height(48.px)
                        .borderRadius(12.px)
                        .backgroundColor(download.accent)
                        .display(DisplayStyle.Flex)
                        .alignItems(AlignItems.Center)
                        .justifyContent(JustifyContent.Center)
                        .color(Colors.White)
                        .fontSize(20.px)
                        .fontWeight(FontWeight.Bold)
                        .toAttrs()
                ) {
                    Text(download.platform.take(1))
                }

                Column(Modifier.gap(4.px)) {
                    H3(
                        Modifier.fontSize(17.px).fontWeight(FontWeight.Bold)
                            .margin(0.px).toAttrs()
                    ) { Text(download.platform) }

                    Span(
                        Modifier.fontSize(12.px).color(Colors.Gray).toAttrs()
                    ) {
                        Text("${download.version} • ${download.size} • ${download.href.take(60)}")
                    }
                }
            }

            Row(modifier = Modifier.gap(8.px)) {
                Button(onClick = { onEdit() }) { Text("Edit") }
                Button(onClick = { confirmDelete = true }) { Text("Delete") }
            }
        }

        P(
            Modifier
                .fontSize(13.px)
                .color(Colors.Gray)
                .margin(top = 12.px, bottom = 0.px)
                .lineHeight(1.5)
                .toAttrs()
        ) {
            Text(
                if (download.description.length > 160) download.description.take(160) + "…"
                else download.description
            )
        }

        if (confirmDelete) {
            Div(
                Modifier
                    .margin(top = 14.px)
                    .padding(12.px)
                    .backgroundColor(Color.rgb(220, 38, 38).withAlpha(0.08f))
                    .borderRadius(10.px)
                    .toAttrs()
            ) {
                P(
                    Modifier.fontSize(14.px).margin(top = 0.px, bottom = 12.px).toAttrs()
                ) {
                    Text("Delete the ${download.platform} download? This cannot be undone.")
                }
                Row(Modifier.gap(10.px)) {
                    Button(onClick = {
                        confirmDelete = false
                        onDelete()
                    }) { Text("Yes, delete") }
                    Button(onClick = { confirmDelete = false }) { Text("Cancel") }
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Editor dialog
// ---------------------------------------------------------------------------
@Composable
private fun DownloadEditorDialog(
    state: DownloadEditState,
    onDismiss: () -> Unit,
    onSave: (DownloadEditState) -> Unit
) {
    var platform by remember { mutableStateOf(state.platform) }
    var version by remember { mutableStateOf(state.version) }
    var size by remember { mutableStateOf(state.size) }
    var accentHex by remember { mutableStateOf(state.accentHex) }
    var href by remember { mutableStateOf(state.href) }
    var description by remember { mutableStateOf(state.description) }
    var warning by remember { mutableStateOf("") }

    Div(
        Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .backgroundColor(Colors.Black.withAlpha(0.6f))
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.Center)
            .padding(24.px)
            .zIndex(1000)
            .toAttrs()
    ) {
        Div(
            Modifier
                .backgroundColor(Colors.White)
                .color(Colors.Black)
                .padding(32.px)
                .borderRadius(16.px)
                .width(660.px)
                .maxWidth(94.vw)
                .maxHeight(88.vh)
                .overflow(Overflow.Auto)
                .boxShadow(0.px, 12.px, 48.px, 0.px, Colors.Black.withAlpha(0.28f))
                .toAttrs()
        ) {
            H3(
                Modifier.fontSize(22.px).fontWeight(FontWeight.Bold)
                    .margin(top = 0.px, bottom = 24.px).toAttrs()
            ) {
                Text(if (state.id == null) "New Download" else "Edit Download")
            }

            // Platform
            DLFieldLabel("Platform")
            Select(
                attrs = Modifier
                    .fillMaxWidth()
                    .padding(10.px)
                    .margin(top = 6.px)
                    .borderRadius(10.px)
                    .border(1.px, LineStyle.Solid, Colors.Black.withAlpha(0.15f))
                    .toAttrs {
                        onChange { platform = it.value.toString() }
                    }
            ) {
                PLATFORM_OPTIONS.forEach { p ->
                    Option(
                        value = p,
                        attrs = Modifier.toAttrs { if (p == platform) attr("selected", "") }
                    ) { Text(p) }
                }
            }

            Row(Modifier.fillMaxWidth().gap(12.px)) {
                Column(Modifier.weight(1f)) {
                    DLTextField("Version", version, "v1.4.0") { version = it }
                }
                Column(Modifier.weight(1f)) {
                    DLTextField("Size", size, "24 MB") { size = it }
                }
            }

            DLTextField("Download URL", href, "https://...") { href = it }

            DLTextArea("Description", description, "Short description shown under the platform name", 3) {
                description = it
            }

            DLSectionLabel("Accent Color", topMargin = 20.px)
            Row(
                Modifier.fillMaxWidth().margin(top = 6.px).gap(8.px).flexWrap(FlexWrap.Wrap)
            ) {
                PRESET_COLORS.forEach { hex ->
                    val isSelected = hex.equals(accentHex, ignoreCase = true)
                    Div(
                        Modifier
                            .width(28.px)
                            .height(28.px)
                            .borderRadius(8.px)
                            .backgroundColor(hexToColor(hex))
                            .border(
                                if (isSelected) 3.px else 1.px,
                                LineStyle.Solid,
                                if (isSelected) Colors.Black else Colors.Black.withAlpha(0.15f)
                            )
                            .onClick { accentHex = hex }
                            .toAttrs()
                    ) {}
                }
            }

            if (warning.isNotEmpty()) {
                Div(
                    Modifier
                        .fillMaxWidth()
                        .margin(top = 20.px)
                        .padding(12.px)
                        .backgroundColor(Color.rgb(245, 158, 11).withAlpha(0.12f))
                        .color(Color.rgb(180, 83, 9))
                        .borderRadius(8.px)
                        .fontSize(13.px)
                        .toAttrs()
                ) { Text(warning) }
            }

            Row(
                Modifier
                    .fillMaxWidth()
                    .margin(top = 28.px)
                    .padding(top = 20.px)
                    .borderTop(1.px, LineStyle.Solid, Colors.Black.withAlpha(0.08f))
                    .gap(12.px),
                horizontalArrangement = Arrangement.End
            ) {
                Button(onClick = { onDismiss() }) { Text("Cancel") }
                Button(
                    enabled = platform.isNotBlank() && href.isNotBlank(),
                    onClick = {
                        if (platform.isBlank() || href.isBlank()) {
                            warning = "Platform and Download URL are required."
                            return@Button
                        }
                        onSave(
                            state.copy(
                                platform = platform,
                                version = version,
                                size = size,
                                accentHex = accentHex,
                                href = href,
                                description = description
                            )
                        )
                    }
                ) {
                    Text(if (state.id == null) "Create" else "Save Changes")
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Small form helpers
// ---------------------------------------------------------------------------
@Composable
private fun DLSectionLabel(text: String, topMargin: CSSLengthValue = 16.px) {
    Div(
        Modifier
            .fillMaxWidth()
            .margin(top = topMargin, bottom = 4.px)
            .padding(top = 12.px)
            .borderTop(1.px, LineStyle.Solid, Colors.Black.withAlpha(0.06f))
            .toAttrs()
    ) {
        Span(
            Modifier.fontSize(12.px).fontWeight(FontWeight.Bold)
                .letterSpacing(0.5.px)
                .color(Color.rgb(99, 102, 241))
                .toAttrs()
        ) { Text(text.uppercase()) }
    }
}

@Composable
private fun DLFieldLabel(text: String) {
    Label(
        attrs = Modifier.fontSize(13.px).fontWeight(FontWeight.Medium).toAttrs()
    ) { Text(text) }
}

@Composable
private fun DLTextField(
    label: String,
    value: String,
    placeholderText: String,
    onValueChange: (String) -> Unit
) {
    Column(Modifier.fillMaxWidth().margin(top = 8.px)) {
        DLFieldLabel(label)
        Input(
            type = InputType.Text,
            attrs = Modifier
                .fillMaxWidth()
                .padding(10.px, 12.px)
                .margin(top = 6.px)
                .borderRadius(10.px)
                .border(1.px, LineStyle.Solid, Colors.Black.withAlpha(0.15f))
                .toAttrs {
                    placeholder(placeholderText)
                    value(value)
                    onInput { onValueChange(it.value) }
                }
        )
    }
}

@Composable
private fun DLTextArea(
    label: String,
    value: String,
    placeholderText: String,
    rows: Int,
    onValueChange: (String) -> Unit
) {
    Column(Modifier.fillMaxWidth().margin(top = 8.px)) {
        DLFieldLabel(label)
        TextArea(
            attrs = Modifier
                .fillMaxWidth()
                .padding(10.px, 12.px)
                .margin(top = 6.px)
                .minHeight((rows * 20).px)
                .borderRadius(10.px)
                .border(1.px, LineStyle.Solid, Colors.Black.withAlpha(0.15f))
                .toAttrs {
                    placeholder(placeholderText)
                    value(value)
                    onInput { onValueChange(it.value) }
                }
        )
    }
}

// Local color-to-hex helper (same body as your backend one)
private fun colorToHexLocal(color: com.varabyte.kobweb.compose.ui.graphics.Color): String {
    return try {
        val rgb = color as com.varabyte.kobweb.compose.ui.graphics.Color.Rgb
        val r = rgb.red.toString(16).padStart(2, '0')
        val g = rgb.green.toString(16).padStart(2, '0')
        val b = rgb.blue.toString(16).padStart(2, '0')
        "#$r$g$b".uppercase()
    } catch (e: Throwable) {
        "#6366F1"
    }
}